import { hash } from 'immutable';
import { combineActions, handleActions } from 'redux-actions';

import type { AsyncDispatch } from '../../utils/redux-store/asyncDispatchMiddleware';
import { accountsActions } from '../accounts';
import { getDeletedAccountIds } from '../accounts/accountsUtils';
import { resourceStoreTypes, resourceUndoUpserters, resourceUpserters } from '../core';
import { coreActions } from '../core-actions';
import { transactionsTransformers, type transactionsTypes } from '../transactions';
import * as actions from './scheduledTransactionsActions';
import { applyScheduleSourceFallback } from './scheduledTransactionsTransformers';
import * as scheduledTransactionsTypes from './scheduledTransactionsTypes';

const isObjectLike = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const getMetaProps = (meta: unknown): Record<string, unknown> | undefined => {
  if (!isObjectLike(meta)) return undefined;
  if (isObjectLike(meta.props)) return meta.props;
  const embeddedMeta = meta.meta;
  if (isObjectLike(embeddedMeta) && isObjectLike(embeddedMeta.props)) return embeddedMeta.props;
  return undefined;
};

const QcsSyncScheduledTransactionsStoreFactory =
  resourceStoreTypes.createTypedQcsSyncResourceStoreFactory<scheduledTransactionsTypes.ScheduledTransactionType>();

const scheduledTransactionsReducer = handleActions<
  resourceStoreTypes.QcsSyncResourceStore<scheduledTransactionsTypes.ScheduledTransactionType>,
  | coreActions.SanitizeAction
  | ReturnType<typeof actions.createScheduledTransaction>
  | ReturnType<typeof actions.createScheduledTransactionSuccess>
  | ReturnType<typeof actions.updateScheduledTransaction>
  | ReturnType<typeof actions.updateScheduledTransactionSuccess>
  | ReturnType<typeof actions.deleteScheduledTransaction>
  | ReturnType<typeof actions.deleteScheduledTransactionSuccess>
  | ReturnType<typeof actions.batchScheduledTransactions>
  | ReturnType<typeof actions.batchScheduledTransactionsSuccess>
  | ReturnType<typeof actions.getScheduledTransactions>
  | ReturnType<typeof actions.getScheduledTransactionsSuccess>
  | ReturnType<typeof actions.getScheduledTransactionsFailure>
  | ReturnType<typeof actions.createBillScoutScheduleSuccess>
>(
  {
    [coreActions.SANITIZE]: (state) =>
      state.set(
        'resourcesById',
        state.resourcesById.map((resource) =>
          resource.mergeDeep({
            transaction: {
              payee: hash(resource.transaction?.payee || '').toString(),
              memo: hash(resource.transaction?.memo || '').toString(),
            } as transactionsTypes.TransactionProps, // NOTE: `mergeDeep` doesn't recursively type nested records as Partial<> like it should, so we typecast here.
          }),
        ),
      ),

    [actions.createScheduledTransaction.type]: (state, { payload: scheduledTransaction }) =>
      resourceUpserters.upsertResource(state, scheduledTransaction),
    [actions.createScheduledTransactionSuccess.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.deleteScheduledTransaction),
    [actions.updateScheduledTransaction.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.updateScheduledTransaction),
    [actions.updateScheduledTransactionSuccess.type]: (state, { payload: scheduledTransaction }) =>
      resourceUpserters.upsertResource(state, scheduledTransaction),
    [actions.deleteScheduledTransaction.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.createScheduledTransaction),
    [actions.deleteScheduledTransactionSuccess.type]: (state, { payload: scheduledTransaction }) =>
      resourceUpserters.upsertResource(state, scheduledTransaction),
    [actions.batchScheduledTransactions.type]: (state, action) =>
      resourceUndoUpserters.upsertResourcesSetupUndo(state, action, actions.batchScheduledTransactions, false),
    [actions.batchScheduledTransactionsSuccess.type]: (state, action) =>
      resourceUndoUpserters.upsertResourcesSetupUndo(state, action, actions.batchScheduledTransactions, true),

    [combineActions(
      actions.createScheduledTransactionFailure,
      actions.updateScheduledTransactionFailure,
      actions.deleteScheduledTransactionFailure,
      actions.batchScheduledTransactionsFailure,
    ).toString()]: (
      state: resourceStoreTypes.QcsSyncResourceStore<scheduledTransactionsTypes.ScheduledTransactionType>,
      { payload: error, asyncDispatch }: { payload: unknown; asyncDispatch: AsyncDispatch },
    ) => {
      asyncDispatch(actions.getScheduledTransactions());
      return resourceUpserters.resetResources(state, error);
    },

    [actions.getScheduledTransactions.type]: (state) => state.merge({ isLoading: true }),
    [actions.getScheduledTransactionsSuccess.type]: (state, { payload: resources }) =>
      resourceUpserters.upsertResources(state, resources),
    [actions.getScheduledTransactionsFailure.type]: (state, { payload: error }) =>
      resourceUpserters.completeWithError(state, error),
    [actions.createBillScoutScheduleSuccess.type]: (state, { payload, meta, asyncDispatch }) => {
      let newState = state;
      if (meta?.response?.data?.schedule) {
        const schedule = meta.response.data.schedule;
        const consumableTxn = transactionsTransformers.transformQcsTransactionToTransaction(
          applyScheduleSourceFallback(schedule),
        );
        if (!consumableTxn) return newState;

        // Some dispatch sites attach prefill data in `action.meta.props`. Be tolerant of embedded meta.
        const metaProps = getMetaProps(meta);
        const sourceIsExcludedFromF2S = metaProps?.sourceIsExcludedFromF2S;
        const sourceIsExcludedFromReports = metaProps?.sourceIsExcludedFromReports;
        const sourceBusinessId = metaProps?.sourceBusinessId;

        let consumableTxnWithSplit = consumableTxn;
        if (typeof sourceIsExcludedFromF2S === 'boolean') {
          consumableTxnWithSplit = consumableTxnWithSplit.set('isExcludedFromF2S', sourceIsExcludedFromF2S);
        }
        if (typeof sourceIsExcludedFromReports === 'boolean') {
          consumableTxnWithSplit = consumableTxnWithSplit.set('isExcludedFromReports', sourceIsExcludedFromReports);
        }
        if (typeof sourceBusinessId === 'string') {
          consumableTxnWithSplit = consumableTxnWithSplit.set('businessId', sourceBusinessId);
        }

        const isSubscriptionCreate = metaProps?.isSubscription === true;
        const shouldUpdateToSubscription = isSubscriptionCreate && consumableTxnWithSplit?.isSubscription === false; // override default isBill value

        const newScheduledTxn = scheduledTransactionsTypes.mkScheduledTransaction({
          // update schedTxns
          ...schedule,
          type: shouldUpdateToSubscription ? scheduledTransactionsTypes.STTypeEnum.SUBSCRIPTION : schedule.type,
          clientId: payload.clientId,
          recurrence: scheduledTransactionsTypes.mkRecurrence({ ...schedule.recurrence }),
          transaction: shouldUpdateToSubscription
            ? consumableTxnWithSplit.merge({ isBill: false, isSubscription: true })
            : consumableTxnWithSplit,
        });
        const createScheduledTransactionAction = {
          ...meta,
          payload: newScheduledTxn,
        };
        // hacky way of updating BillScout to use the correct flag when creating a subscription instead of a bill
        if (shouldUpdateToSubscription) {
          asyncDispatch(
            actions.updateScheduledTransaction(
              scheduledTransactionsTypes.mkScheduledTransaction({
                id: newScheduledTxn.id,
                transaction: newScheduledTxn.transaction,
              }),
            ),
          );
        }
        newState = resourceUndoUpserters.upsertResourceSetupUndo(
          state,
          createScheduledTransactionAction,
          actions.deleteScheduledTransaction,
        );
      }

      return newState;
    },

    // accounts cascade deletion
    [accountsActions.applyAccountsChanges.toString()]: (
      state: resourceStoreTypes.QcsSyncResourceStore<scheduledTransactionsTypes.ScheduledTransactionType>,
      {
        payload,
        asyncDispatch,
      }: ReturnType<typeof accountsActions.applyAccountsChanges> & { asyncDispatch: AsyncDispatch },
    ) => {
      const deletedAccountIds = getDeletedAccountIds(payload);
      if (deletedAccountIds.length) {
        asyncDispatch(actions.getScheduledTransactions(undefined, { context: 'applyAccountsChanges' })); /// TODO: remove after bump is enabled
        const resourcesById = state.resourcesById.filter(
          (scheduledTransaction: scheduledTransactionsTypes.ScheduledTransactionType) =>
            !deletedAccountIds.includes(scheduledTransaction.transaction?.accountId || ''),
        );
        return state.merge({ resourcesById });
      }
      return state;
    },
  },
  QcsSyncScheduledTransactionsStoreFactory(),
);

export default scheduledTransactionsReducer;
